import React, { useMemo } from 'react';
import {
  ColorValues,
  Flex,
  IconBrandIconToday,
  PrimaryButton,
  ScrollView,
  Spinner,
  Switch,
  Typography,
  useBottomSheetEntered,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { useGoodsOptionsBrief } from '../../queries/useGoodsOptionsBrief';
import { SelectOptionsBottomBanner } from './SelectOptionsBottomBanner';
import { SelectOptionItemView } from './SelectOptionItemView';
import type { GoodsOption } from 'shared/src/api';
import { bridge, isAndroid, isNil, isNotNil } from 'shared/src/utils';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';
import { OptionRecommendView } from '../OptionRecommendView';
import { useLogGoodsAnalyticsEvent } from '../../../../hooks/useLogGoodsAnalyticsEvent';
import { useSelectedOptions } from '../../../../store/selectedOptions.atom';
const FIRST_DEPTH = 1;

export const SelectOptionView: React.FC<{
  onSelectOptionComplete: (option: GoodsOption) => void;
  onPressClose: () => void;
}> = props => {
  const [isTodayOptionsOnly, setIsTodayOptionsOnly] = React.useState(false);
  const { data: goodsOptionsBrief } = useGoodsOptionsBrief();
  const [selectedOptions, setSelectedOptions] = useSelectedOptions();
  const [currentStep, setCurrentStep] = React.useState(() => {
    if (!isAndroid) return FIRST_DEPTH;
    const depths = Object.keys(selectedOptions).map(Number);
    return depths.length > 0 ? Math.max(...depths) + 1 : FIRST_DEPTH;
  });
  const entered = useBottomSheetEntered();

  const optionGroups = useMemo(
    () => goodsOptionsBrief?.option_groups ?? [],
    [goodsOptionsBrief?.option_groups],
  );

  const isPreOrder = goodsOptionsBrief?.sales_method === 'PRE_ORDER';

  const isDeliveryTypeIsToday =
    goodsOptionsBrief?.goods_delivery_type === 'today' ||
    goodsOptionsBrief?.goods_delivery_type === 'shak';

  const isRenderTodayDeliveryToggle = !isPreOrder && isDeliveryTypeIsToday;

  const isGiftMode = useIsBottomSheetGiftMode();
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();

  return (
    <Flex flex={1}>
      {isGiftMode ? (
        <Flex padding={{ vertical: 16, horizontal: 16 }}>
          <Flex
            justifyContent={'center'}
            height={40}
            backgroundColor={'blue5'}
            borderRadius={4}
            padding={{ horizontal: 16 }}
          >
            <Typography type={'body2'} color={'content_slate_primary'}>
              선물 옵션은 1가지만 선택할 수 있어요
            </Typography>
          </Flex>
        </Flex>
      ) : (
        <Flex>
          {isRenderTodayDeliveryToggle ? (
            <Flex
              padding={{ vertical: 16, horizontal: 16 }}
              alignItems={'flex-end'}
            >
              <Flex.Row
                alignItems={'center'}
                justifyContent={'center'}
                onPress={() => {
                  const nextValue = !isTodayOptionsOnly;
                  setIsTodayOptionsOnly(nextValue);
                  setCurrentStep(FIRST_DEPTH);
                }}
              >
                <Flex padding={{ top: 2 }}>
                  <Switch
                    value={isTodayOptionsOnly}
                    color={'lilac'}
                    scale={isAndroid ? 1.0 : 0.6}
                    onChange={value => {
                      setIsTodayOptionsOnly(value);
                      setCurrentStep(FIRST_DEPTH);
                      logGoodsAnalytics({
                        name: 'CLICK_TODAY_OPTION_ONLY_SWITCH',
                        analytics: {
                          IS_ACTIVATED: value ? '1' : '0',
                        },
                      });
                    }}
                    testID={'오늘출발토글'}
                  />
                </Flex>
                <Flex.Row alignItems={'center'} gap={4}>
                  <IconBrandIconToday color={ColorValues.lilac40} />
                  <Typography type={'body3'} color={'content_secondary'}>
                    옵션만 보기
                  </Typography>
                </Flex.Row>
              </Flex.Row>
            </Flex>
          ) : (
            <VerticalSpacing size={16} />
          )}
        </Flex>
      )}

      {entered ? (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingBottom: 16,
            gap: 12,
          }}
        >
          <OptionRecommendView
            onPressSelectOption={props.onSelectOptionComplete}
          />

          {optionGroups.map((option, index) => (
            <SelectOptionItemView
              key={option.depth}
              onlyShowTodayDelivery={isTodayOptionsOnly}
              isOpen={currentStep === option.depth}
              index={index}
              option={option}
              onPressMenu={() => {
                if (option.depth === currentStep) {
                  setCurrentStep(0);
                  return;
                }
                const beforeSteps = Array.from(
                  { length: option.depth - 1 },
                  (_, i) => i + 1,
                );
                const hasNullStep = beforeSteps
                  .map(i => isNil(selectedOptions[i]))
                  .filter(flag => flag);

                if (hasNullStep.length > 0) {
                  bridge.toast('상위 옵션을 선택해주세요');
                  return;
                }

                setCurrentStep(option.depth);
              }}
              selectedOptions={selectedOptions}
              onSelectOption={selected => {
                setSelectedOptions(prev => ({
                  ...prev,
                  [option.depth]: selected,
                }));
                if (selected.is_final_depth) {
                  if (isNotNil(selected.goods_option)) {
                    props.onSelectOptionComplete(selected.goods_option);
                    if (isAndroid) {
                      setSelectedOptions({});
                    }
                  }
                  return;
                }

                setCurrentStep(prev => prev + 1);
              }}
            />
          ))}
        </ScrollView>
      ) : (
        <Flex.Center flex={1}>
          <Spinner />
        </Flex.Center>
      )}

      <SelectOptionsBottomBanner />
      <Flex
        padding={{ horizontal: 12, vertical: 12 }}
        borderColor={'border_tertiary'}
        borderTopWidth={1}
      >
        <PrimaryButton
          variant={'fill'}
          size={'xlarge'}
          outline={true}
          testID={'옵션선택닫기버튼'}
          onPress={props.onPressClose}
        >
          옵션 선택 닫기
        </PrimaryButton>
      </Flex>
    </Flex>
  );
};
